// src/finance/donut.ts
// Parts et géométrie du donut de répartition (« Qui paie quoi »).
// Aucun calcul fiscal : tout dérive de aggregatePayers.
import type { RegimeResult } from './engine/types'
import { round2 } from './engine/tax'
import { aggregatePayers } from './payers'

export type DonutGroup = 'expenses' | 'levies' | 'living' | 'remaining'

export type DonutSlice = {
  key: string
  label: string
  group: DonutGroup
  amount: number
  /**
   * Part du CA RÉEL (`r.revenueMonthly`), donc directement lisible comme
   * « X % du CA ». La somme des parts vaut 1 quand le CA couvre tout, et
   * DÉPASSE 1 en cas de découvert : l'excédent est exactement `overdraft`.
   */
  share: number
}

export type DonutData = {
  /** Postes du CA, dans l'ordre du flux d'argent. */
  slices: DonutSlice[]
  /** CA réel du scénario : la référence des pourcentages ET le total affiché. */
  total: number
  /** Somme des postes retenus. Vaut `total` sans découvert, davantage sinon. */
  spent: number
  /**
   * Ce qui manque pour couvrir tous les postes — 0 quand le CA couvre tout.
   *
   * Calculé sur le RESTE (`living − net`) et non sur `spent − total` : les sept
   * autres postes somment exactement au CA par construction, donc le seul poste
   * qui puisse créer un découvert est le train de vie. Passer par `spent`, somme
   * de huit montants déjà arrondis, dérivait d'un centime et déclenchait l'alerte
   * orange sur des budgets couverts (106 scénarios réalistes sur 630 balayés),
   * en plus d'annoncer 1 000,01 € là où LivingBudget annonçait 1 000,00 €.
   */
  overdraft: number
  /**
   * Reste après dépenses perso, SIGNÉ. Négatif, il n'a pas d'arc — mais la vue
   * doit tout de même l'afficher : c'est la grandeur que l'utilisateur vient
   * chercher, et elle disparaissait purement de la légende et de l'aria-label.
   */
  remaining: number
  /** true si le CA ne couvre même pas les dépenses société. */
  isLoss: boolean
  /** Manque à gagner sur les dépenses société (0 hors perte). */
  deficit: number
}

/**
 * Les huit postes du CA, dans l'ordre du flux d'argent, plus les repères qui
 * permettent de les lire sans être trompé.
 *
 * Les postes nuls sont omis (un arc invisible et une ligne de légende vide
 * n'apprennent rien), de même qu'un reste négatif — un arc ne peut pas être
 * négatif. Le montant, lui, reste exposé par `remaining` : il doit être LU même
 * quand il ne peut pas être TRACÉ.
 *
 * `share` est rapporté au CA réel et JAMAIS à la somme des postes retenus :
 * diviser par les postes retenus normalisait à 1 un total qui, une fois le
 * reste négatif écarté, dépassait le CA — un poste valant 65,7 % du CA
 * s'affichait alors à 58,0 %, sous un total annoncé « CA réparti » supérieur
 * au CA. `total` / `spent` / `overdraft` rendent l'écart explicite au lieu de
 * le dissoudre.
 *
 * `scenarioLabel` qualifie le poste « Ce qui te reste » du scénario et de la
 * juridiction affichés : sans lui, deux « ce qui te reste » calculés sur des
 * scénarios différents cohabitent à l'écran sous des libellés identiques.
 */
export function donutData(
  r: RegimeResult,
  personalExpensesMonthly: number,
  corporateTaxLabel: string,
  scenarioLabel?: string,
  /**
   * Commission Apple incluse dans les dépenses société du régime. Fournie, elle
   * est SORTIE du poste « dépenses saisies » pour devenir un poste à part : à 26
   * ou 30 % du brut, c'est le plus gros coût d'exploitation d'une app, et le
   * fondre dans les autres dépenses le rendrait invisible.
   */
  appCommissionMonthly = 0,
): DonutData {
  const a = aggregatePayers(r)
  const total = r.revenueMonthly
  // À perte, il n'y a plus rien à répartir : le seul poste survivant serait les
  // dépenses société, qui excèdent le CA — un unique arc à « 100 % » pour un
  // montant supérieur au CA. On rend donc zéro part et on laisse la vue afficher
  // le déficit réel.
  if (r.isLoss) {
    return {
      slices: [],
      total,
      spent: 0,
      overdraft: 0,
      remaining: 0,
      isLoss: true,
      deficit: round2(Math.max(0, a.expenses - total)),
    }
  }
  const living = Math.max(0, personalExpensesMonthly)
  const remaining = round2(a.net - living)
  const commission = Math.max(0, Math.min(appCommissionMonthly, a.expenses))
  const candidates: Omit<DonutSlice, 'share'>[] = [
    // `a.expenses` contient déjà la commission (le moteur l'a reçue en charge) :
    // on la retranche ici pour ne pas la compter deux fois.
    { key: 'companyExpenses', label: 'Dépenses société', group: 'expenses', amount: round2(a.expenses - commission) },
    { key: 'appCommission', label: 'Commission Apple', group: 'expenses', amount: commission },
    { key: 'corporateTax', label: corporateTaxLabel, group: 'levies', amount: a.company.corporateTax },
    { key: 'employerContrib', label: 'Cotisations patronales', group: 'levies', amount: a.company.employerContrib },
    { key: 'employeeContrib', label: 'Cotisations salariales', group: 'levies', amount: a.personal.employeeContrib },
    { key: 'ir', label: 'Impôt sur le revenu', group: 'levies', amount: a.personal.ir },
    { key: 'pfu', label: 'PFU sur dividendes', group: 'levies', amount: a.personal.pfu },
    { key: 'personalExpenses', label: 'Dépenses perso', group: 'living', amount: living },
    {
      key: 'remaining',
      label: scenarioLabel === undefined ? 'Ce qui te reste' : `Ce qui te reste — ${scenarioLabel}`,
      group: 'remaining',
      amount: remaining,
    },
  ]
  const kept = candidates.filter((s) => s.amount > 0)
  const spent = round2(kept.reduce((t, s) => t + s.amount, 0))
  return {
    slices: kept.map((s) => ({ ...s, share: total > 0 ? s.amount / total : 0 })),
    total,
    spent,
    overdraft: round2(Math.max(0, living - a.net)),
    remaining,
    isLoss: false,
    deficit: 0,
  }
}

/** Point du cercle, origine à midi et sens horaire. */
function point(cx: number, cy: number, r: number, angle: number): string {
  return `${(cx + r * Math.sin(angle)).toFixed(2)} ${(cy - r * Math.cos(angle)).toFixed(2)}`
}

/**
 * Anneau fermé entre deux rayons. Chaque arc est tracé en DEUX moitiés : aucune
 * ne dépasse π, donc le drapeau large-arc vaut toujours 0, et une part unique à
 * 100 % (arc de 360°, dont les extrémités seraient confondues) se trace sans
 * traitement particulier.
 */
export function arcPath(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  startAngle: number,
  endAngle: number,
): string {
  const mid = (startAngle + endAngle) / 2
  return [
    `M ${point(cx, cy, rOuter, startAngle)}`,
    `A ${rOuter} ${rOuter} 0 0 1 ${point(cx, cy, rOuter, mid)}`,
    `A ${rOuter} ${rOuter} 0 0 1 ${point(cx, cy, rOuter, endAngle)}`,
    `L ${point(cx, cy, rInner, endAngle)}`,
    `A ${rInner} ${rInner} 0 0 0 ${point(cx, cy, rInner, mid)}`,
    `A ${rInner} ${rInner} 0 0 0 ${point(cx, cy, rInner, startAngle)}`,
    'Z',
  ].join(' ')
}
